import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const List = ({ title, rows, render, testid }) => (
  <section className="rounded-[10px] border border-[#e7e4db] bg-[#fffefa] p-4" data-testid={testid}>
    <h2 className="text-sm font-semibold text-[#20211d]">{title} · {rows.length}</h2>
    <ul className="mt-2 divide-y divide-[#ecebe4] text-xs text-[#55574e]">
      {rows.map(r => <li key={r.id} className="py-2 font-mono break-all">{render(r)}</li>)}
    </ul>
  </section>
);

export default function AdminDataCleanupPage() {
  const { token, user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [backup, setBackup] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { setPlan((await axios.get(`${API_URL}/api/admin/data-cleanup/feb24-test-requests`, { headers: { Authorization: `Bearer ${token}` } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not load the cleanup preview'); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/data-cleanup/feb24-test-requests`, { confirm_phrase: phrase, backup_confirmed: backup }, { headers });
      setResult(res.data); toast.success('Test requests removed'); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cleanup failed'); }
    finally { setBusy(false); }
  };

  if (user?.role !== 'master_admin') return <p className="p-8 text-sm" data-testid="cleanup-forbidden">Only a master admin can open this page.</p>;
  if (!plan) return <div className="p-8"><Loader2 className="h-5 w-5 animate-spin text-[#F56F05]" /></div>;
  const n = plan.counts.change_requests + plan.counts.change_request_events + plan.counts.review_items;
  const ready = !plan.blockers.length && plan.counts.change_requests > 0;

  return (
    <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="data-cleanup-page">
      <Link to="/admin/settings" className="inline-flex items-center gap-1 text-xs text-[#77776e]"><ArrowLeft className="h-3.5 w-3.5" /> Settings</Link>
      <header>
        <p className="text-[10px] font-bold tracking-[0.1em] text-[#74776a]">ONE-TIME DATA CLEANUP</p>
        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#20211d]">Feb 24 test scheduling requests</h1>
        <p className="mt-2 text-sm text-[#55574e]">Removes only the test requests made on Feb 24, 2026 by the deleted demo client, for visits that no longer exist. Each record is copied to a cleanup archive before removal. Notifications and audit history that mention them are kept.</p>
      </header>
      {plan.blockers.map(b => <p key={b} className="rounded-[8px] border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="cleanup-blocker">{b}</p>)}
      {plan.counts.change_requests === 0 && <p className="rounded-[8px] border border-[#e7e4db] bg-[#faf9f6] p-3 text-sm" data-testid="cleanup-nothing">{plan.already_done ? 'This cleanup has already been run. Removed records are kept in the cleanup archive.' : 'No matching test requests were found. Nothing will be removed.'}</p>}
      {plan.counts.change_requests > 0 && (
        <>
          <p className="text-sm text-[#20211d]" data-testid="cleanup-summary">Will remove <b>{n}</b> records: {plan.counts.change_requests} requests, {plan.counts.change_request_events} request history entries and {plan.counts.review_items} Needs Review items. Kept: {plan.linked_records_kept.notifications} notifications and {plan.linked_records_kept.admin_audit_logs} audit entries.</p>
          <List testid="cleanup-requests" title="Requests" rows={plan.change_requests} render={r => `${r.id} · ${r.request_type} · ${r.status} · ${String(r.created_at).slice(0, 19)} · ${r.reason || ''}`} />
          <List testid="cleanup-reviews" title="Needs Review items" rows={plan.review_items} render={r => `${r.id} · ${r.title} · ${r.status} · request ${r.legacy_change_request_id}`} />
          <List testid="cleanup-events" title="Request history entries" rows={plan.change_request_events} render={r => `${r.id} · ${r.event_type} · request ${r.change_request_id}`} />
        </>
      )}
      {ready && (
        <section className="rounded-[10px] border border-[#f1c7a8] bg-[#fff7f0] p-4 space-y-3" data-testid="cleanup-confirm">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#a3471d]"><ShieldAlert className="h-4 w-4" /> Before you remove anything</p>
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#F56F05]" checked={backup} onChange={e => setBackup(e.target.checked)} data-testid="cleanup-backup-confirmed" />
            A fresh production database backup has been taken today.
          </label>
          <label className="block text-xs text-[#55574e]">Type <b>{plan.confirm_phrase}</b> to confirm
            <Input value={phrase} onChange={e => setPhrase(e.target.value)} className="mt-1" data-testid="cleanup-phrase" />
          </label>
          <Button onClick={run} disabled={busy || !backup || phrase.trim() !== plan.confirm_phrase} className="bg-[#a3471d] hover:bg-[#86391a] text-white" data-testid="cleanup-run">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Remove {n} test records
          </Button>
        </section>
      )}
      {result && <p className="rounded-[8px] border border-[#cfdcc0] bg-[#f3f7ee] p-3 text-sm" data-testid="cleanup-result">Done. Removed {result.removed.change_requests} requests, {result.removed.change_request_events} history entries and {result.removed.review_items} Needs Review items. Archive run {result.run_id}.</p>}
    </main>
  );
}
